import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

// Connects to data-controller="period-filter" -- the P&L dashboard's
// Sellerboard-style "Period" dropdown. Each preset item selects a GROUP of
// tiles (e.g. "Today / Yesterday / Month to date / Last month"), not a
// single period -- picking one navigates immediately (mirroring
// date_range_controller.js's feel on the PPC pages), and the server
// re-renders that many tile cards. "Custom range" swaps the menu into a
// two-month flatpickr range calendar, synced with visible Start/End text
// inputs, requiring an explicit Apply since a two-click range selection
// can't auto-submit after the first click.
export default class extends Controller {
  static targets = ["toggle", "menu", "presetsPane", "calendarPane", "calendarMount", "startInput", "endInput", "applyButton"]
  static values = { dateFrom: String, dateTo: String, today: String, baseUrl: String }

  connect() {
    // If the page loaded directly into a custom range (the calendar pane
    // rendered visible server-side, see _period_filter.html.erb), the
    // calendar still needs to be constructed at some point -- but NOT
    // immediately here. The Bootstrap dropdown itself always starts closed
    // (display:none) on a fresh page load regardless of which pane is
    // showing inside it, and flatpickr measures its own width from
    // offsetWidth, which is always 0 for anything inside a display:none
    // ancestor -- constructing here silently produces a near-zero-width,
    // effectively invisible calendar that never fixes itself once the
    // dropdown is later opened (confirmed: reopening "Custom range" after
    // Apply-navigating into it showed nothing, since ensureCalendar()'s own
    // `if (this._fp) return` guard means it's never retried). Deferring to
    // the dropdown's own shown.bs.dropdown event guarantees construction
    // only ever happens once the container is genuinely laid out.
    if (!this.calendarPaneTarget.classList.contains("d-none")) {
      this.element.addEventListener("shown.bs.dropdown", () => this.ensureCalendar(), { once: true })
    }
  }

  disconnect() {
    this._fp?.destroy()
  }

  applyGroup(event) {
    window.location.href = `${this.baseUrlValue}?group=${event.currentTarget.dataset.group}`
  }

  showCalendar() {
    this.presetsPaneTarget.classList.add("d-none")
    this.calendarPaneTarget.classList.remove("d-none")
    this.menuTarget.classList.add("period-filter-calendar-mode")
    this.ensureCalendar()
  }

  showPresets() {
    this.calendarPaneTarget.classList.add("d-none")
    this.presetsPaneTarget.classList.remove("d-none")
    this.menuTarget.classList.remove("period-filter-calendar-mode")
  }

  ensureCalendar() {
    if (this._fp) return

    this.startInputTarget.value = this.dateFromValue
    this.endInputTarget.value = this.dateToValue
    this._fp = flatpickr(this.calendarMountTarget, {
      inline: true,
      mode: "range",
      showMonths: 2,
      dateFormat: "Y-m-d",
      maxDate: this.todayValue,
      defaultDate: [ this.dateFromValue, this.dateToValue ],
      onChange: (selectedDates) => this.onCalendarChange(selectedDates)
    })
  }

  onCalendarChange(selectedDates) {
    if (selectedDates[0]) this.startInputTarget.value = flatpickr.formatDate(selectedDates[0], "Y-m-d")
    if (selectedDates[1]) this.endInputTarget.value = flatpickr.formatDate(selectedDates[1], "Y-m-d")
    this.applyButtonTarget.disabled = selectedDates.length < 2
  }

  startInputChanged() {
    this.syncCalendarFromInputs()
  }

  endInputChanged() {
    this.syncCalendarFromInputs()
  }

  syncCalendarFromInputs() {
    const start = this.startInputTarget.value
    const end = this.endInputTarget.value || start
    if (!start) return

    this._fp.setDate([ start, end ], true)
    this.applyButtonTarget.disabled = !(start && this.endInputTarget.value)
  }

  apply() {
    const params = new URLSearchParams({ date_from: this.startInputTarget.value, date_to: this.endInputTarget.value })
    window.location.href = `${this.baseUrlValue}?${params.toString()}`
  }

  cancel() {
    this.showPresets()
    bootstrap.Dropdown.getOrCreateInstance(this.toggleTarget).hide()
  }
}
